<template>
  <div class="markdown-editor-wrapper" :class="{ 'preview-mode': preview }">
    <mavon-editor
      v-model="innerContent"
      :subfield="!preview"
      :placeholder="preview ? '' : placeholder"
      :toolbars="toolbars"
      :defaultOpen="preview ? 'preview' : 'edit'"
      @imgAdd="handleImageUpload"
      @imgDel="handleImageDelete"
      ref="mdEditor"
      :style="{ height: height }"
    />
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getUploadUrl } from '@/utils/uploadUrl'

// ---------- Props ----------
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  uploadModule: {
    type: String,
    default: 'wiki'
  },
  uploadUrl: {
    type: String,
    default: () => getUploadUrl()
  },
  height: {
    type: String,
    default: '500px'
  },
  preview: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '开始编写 Markdown 内容...'
  }
})

// ---------- Emits ----------
const emit = defineEmits(['update:modelValue'])

// ---------- 内部状态 ----------
const mdEditor = ref(null)
const innerContent = ref(props.modelValue)

watch(() => props.modelValue, (newVal) => {
  innerContent.value = newVal
})

watch(innerContent, (newVal) => {
  emit('update:modelValue', newVal)
})

// ---------- 工具栏配置 ----------
const toolbars = reactive({
  bold: true,
  italic: true,
  header: true,
  underline: true,
  strikethrough: true,
  mark: true,
  superscript: true,
  subscript: true,
  quote: true,
  ol: true,
  ul: true,
  link: true,
  imagelink: true,
  code: true,
  table: true,
  fullscreen: true,
  readmodel: true,
  htmlcode: true,
  help: true,
  undo: true,
  redo: true,
  trash: true,
  save: true,
  navigation: true,
  alignleft: true,
  aligncenter: true,
  alignright: true,
  subfield: true,
  preview: true,
})

// 预览模式下禁用所有工具栏
if (props.preview) {
  Object.keys(toolbars).forEach(key => toolbars[key] = false)
}

// ---------- 图片上传 ----------
const handleImageUpload = async (filename, file) => {
  // 预览模式下不上传
  if (props.preview) return

  const formData = new FormData()
  formData.append('file', file)
  formData.append('module', props.uploadModule)

  try {
    const token = localStorage.getItem('token')
    const response = await fetch(props.uploadUrl, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    })
    const result = await response.json()

    if (result.code === 200 && result.data?.url) {
      mdEditor.value.$img2Url(filename, result.data.url)
      ElMessage.success('图片上传成功')
    } else {
      ElMessage.error(result.message || '图片上传失败')
    }
  } catch (error) {
    console.error('上传错误：', error)
    ElMessage.error('上传图片异常')
  }
}

const handleImageDelete = (filename) => {
  console.log('图片已删除（编辑器中）：', filename)
}

// 暴露方法
defineExpose({
  getEditor: () => mdEditor.value
})
</script>

<style scoped>
.markdown-editor-wrapper {
  width: 100%;
}

/* ========== 预览模式样式 ========== */
.preview-mode :deep(.v-note-toolbar) {
  display: none !important;
}
.preview-mode :deep(.v-note-edit) {
  display: none !important;
}
.preview-mode :deep(.v-note-show) {
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}
.preview-mode :deep(.v-note-wrapper) {
  border: none !important;
  box-shadow: none !important;
}
</style>